import {
    computed, Directive, effect, ElementRef, forwardRef,
    inject, OnChanges, OnInit, PLATFORM_ID, Renderer2, Signal, SimpleChanges
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

/**
 * libs
 */
import { Arabic } from 'flatpickr/dist/l10n/ar.js';
import { english } from 'flatpickr/dist/l10n/default.js';
import { CustomLocale } from 'flatpickr/dist/types/locale';
import { FlatpickrDefaults, FlatpickrDirective } from 'angularx-flatpickr';
import { FlatPickrDayCreateOutputOptions } from 'node_modules/angularx-flatpickr/lib/flatpickr.directive';

/**
 * utilities
 */
import { toArabicNumerals } from '@core/utilities';

/**
 * services
 */
import { ILayoutLang, LangService, LayoutLang } from '@core/services';

/**
 * Language-aware `mwlFlatpickr` — extends the library directive and takes over its selector,
 * so importing this one directive is all a component needs.
 *
 * `provideFlatpickrDefaults()` covers the static options (arrows, formats) and still applies:
 * that defaults object is read once while the instance is built. It cannot cover anything that
 * has to react to a language switch, and it cannot cover `onDayCreate` at all — the base
 * directive always overwrites that key with its own `flatpickrDayCreate` emitter. Both live here:
 *
 * - seeds the flatpickr locale before the instance is created, then re-sets it on every
 *   language change (the app switches language in place, without a reload)
 * - rewrites day numbers to Arabic-Indic numerals (٠–٩) while the active language is Arabic
 *
 * Every input/output/host listener of `FlatpickrDirective` is inherited. Class-level metadata
 * is not, so the selector, `exportAs`, and the `NG_VALUE_ACCESSOR` provider are re-declared
 * below — without the last one, `formControlName` on a picker would silently stop binding.
 *
 * @example
 * ```html
 * <input type="text" mwlFlatpickr [static]="true" formControlName="assembly_date" />
 * ```
 */
@Directive({
    selector: '[mwlFlatpickr]',
    exportAs: 'mwlFlatpickr',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FlatpickrLocaleDirective),
            multi: true,
        },
    ],
})
export class FlatpickrLocaleDirective extends FlatpickrDirective implements OnInit, OnChanges {

    /**
     * Options flatpickr only reads while building its markup.
     *
     * The base directive answers every input change with `instance.set(key, value)`, which
     * updates `config` and redraws the day grid — it never rebuilds the DOM. These keys decide
     * which elements `build()` creates in the first place (the time container, the calendar
     * body, the second input, the range wiring), so `set()` leaves the picker visually stale:
     * flipping `enableTime` on an existing instance stores the flag and shows nothing.
     *
     * Changes to any of these are handled by tearing the instance down and re-creating it.
     */
    private static readonly _REBUILD_ON: readonly string[] = [
        'enableTime',
        'enableSeconds',
        'noCalendar',
        'time24hr',
        'mode',
        'inline',
        'altInput',
        'weekNumbers',
        'showMonths',
    ];

    /** Source of the active app language. */
    private readonly _langService = inject(LangService);

    /** Reactive snapshot of the active app language. */
    private readonly _lang: Signal<ILayoutLang> = toSignal(
        this._langService.changeLang$,
        { initialValue: this._langService.lang }
    );

    /** flatpickr locale matching the active app language. */
    private readonly _locale: Signal<CustomLocale> = computed(() =>
        this._lang() === LayoutLang.Ar ? Arabic : english
    );

    /**
     * Feeds the base directive the dependencies it declares as constructor parameters —
     * it predates `inject()`, so they are resolved here and passed through.
     */
    constructor() {

        super(
            inject(ElementRef),
            inject(FlatpickrDefaults),
            inject(Renderer2),
            inject(PLATFORM_ID),
        );

        // localize day numerals — fires per day cell and again on every redraw
        this.flatpickrDayCreate
            .pipe(takeUntilDestroyed())
            .subscribe((info: FlatPickrDayCreateOutputOptions) => this._localizeDayNumber(info));

        // follow later language switches — `set('locale', ...)` re-runs setupLocale
        // and redraws, so the day cells (and their numerals) are rebuilt in place
        effect(() => {
            const _locale = this._locale();

            if (!this.instance) return;

            this.instance.set('locale', _locale);
        });
    }

    /**
     * Seeds the locale before the picker is created.
     *
     * `ngOnInit` runs ahead of the inherited `ngAfterViewInit`, so the value is picked up by
     * the initial `flatpickr()` call and the calendar never renders in the wrong language.
     */
    public ngOnInit(): void {
        this.locale = this._locale();
    }

    /**
     * Re-creates the picker when a structural option changes, and defers to the base
     * directive's `set()` path for everything else.
     *
     * Nothing runs before the first `ngAfterViewInit`: without an instance there is no stale
     * markup to fix, and the new values are read by the initial build anyway.
     *
     * The selection is carried across by hand — `destroy()` restores the bare input, so the
     * value would otherwise be dropped on the way through. It is re-applied with
     * `triggerChange = false`, because the user did not change anything; only the rendering did.
     */
    public override ngOnChanges(changes: SimpleChanges): void {
        const needsRebuild = Object.keys(changes)
            .some((key) => FlatpickrLocaleDirective._REBUILD_ON.includes(key));

        if (!this.instance || !needsRebuild) {
            super.ngOnChanges(changes);
            return;
        }

        const selectedDates: Date[] = [...this.instance.selectedDates];

        this.instance.destroy();
        this.ngAfterViewInit();

        // the fresh instance builds from `defaultDate: this.initialValue`, which is only ever
        // the pre-init value written by `writeValue` — restore what was actually selected
        if (selectedDates.length) this.instance.setDate(selectedDates, false);
    }

    /**
     * Rewrites a day cell's number to Arabic-Indic numerals while the active language is
     * Arabic; leaves Western digits untouched otherwise.
     */
    private _localizeDayNumber({ dayElement }: FlatPickrDayCreateOutputOptions): void {
        if (this._lang() !== LayoutLang.Ar) return;

        dayElement.textContent = toArabicNumerals(dayElement.textContent ?? '');
    }

}
